import {
  AppBar as AppBarMui,
  Box,
  Skeleton,
  Toolbar as ToolbarMui,
  Typography,
  alpha,
  styled,
  useTheme,
} from "@mui/material";
import { MekiDocIcon as MekiDocIconComponent } from "../icons";
import { SearchBarContainer } from "./Search";
import { useNavigate } from "react-router-dom";

const NewMekiDocIcon = styled(MekiDocIconComponent)(() => ({
  width: "110px",
  height: "20px",
  cursor: "pointer",
}));

// No fixed width: a title longer than the bar wraps instead of pushing the
// page wider than a phone's screen.
const Title = styled(Typography)(() => ({
  fontSize: "19px",
  fontWeight: 600,
  lineHeight: "29px",
  textAlign: "center",
}));

const AppBar = styled(AppBarMui)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const Toolbar = styled(ToolbarMui)(({ theme }) => ({
  padding: 12,
  gap: 6,
  backgroundColor: theme.palette.background.paper,
  height: 64,
  minHeight: 64,
}));

const LeftContainer = styled(Box)({
  display: "flex",
  gap: 12,
  justifyContent: "flex-start",
  alignItems: "center",
  flex: 1,
});

const RightContainer = styled(Box)({
  display: "flex",
  gap: 12,
  justifyContent: "flex-end",
  alignItems: "center",
  flex: 1,
});

// As wide as its content and shrinking when the bar runs out of room; the
// sides grow equally from zero, so the content stays centred.
const CenterContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 12,
  justifyContent: "center",
  alignItems: "center",
  flex: "0 1 auto",
  minWidth: 0,
}));

const SkeletonContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "auto",
  minHeight: "56px",
});

interface HeaderProps {
  title?: string;
  titleColor?: string;
  useMekiDocIcon?: boolean;
  left?: React.ReactNode;
  right?: React.ReactNode;
  searchBar?: React.ReactNode;
}
export function Header({
  title,
  titleColor,
  useMekiDocIcon,
  left,
  right,
  searchBar,
}: HeaderProps) {
  const navigate = useNavigate();
  return (
    <AppBar elevation={0} position="sticky" color="transparent">
      <Toolbar disableGutters>
        {left && <LeftContainer>{left}</LeftContainer>}
        {searchBar ? (
          <SearchBarContainer>{searchBar}</SearchBarContainer>
        ) : (
          <CenterContainer>
            {useMekiDocIcon ? (
              <NewMekiDocIcon
                onClick={() => {
                  navigate("/app/solicitudes");
                }}
              />
            ) : title !== "" ? (
              <Title color={titleColor ?? "primary"}>{title}</Title>
            ) : (
              <SkeletonContainer>
                <Skeleton variant="text" width={"175px"} height={"32px"} />
              </SkeletonContainer>
            )}
          </CenterContainer>
        )}
        <RightContainer>{right}</RightContainer>
      </Toolbar>
    </AppBar>
  );
}

const LoginToolBar = styled(Toolbar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  paddingLeft: 12,
  paddingRight: 12,
}));

export const FormHeader = ({
  left,
  right,
  title,
  titleColor,
  useMekiDocIcon,
}: HeaderProps) => {
  const navigate = useNavigate();
  const { palette } = useTheme();
  return (
    <AppBar position="sticky">
      <LoginToolBar disableGutters>
        <LeftContainer>{left}</LeftContainer>
        <CenterContainer>
          {useMekiDocIcon ? (
            <NewMekiDocIcon
              fill={palette.background.paper}
              onClick={() => {
                navigate("/app/solicitudes");
              }}
            />
          ) : title !== "" ? (
            <Title color={titleColor ?? "primary"}>{title}</Title>
          ) : (
            <SkeletonContainer>
              <Skeleton variant="text" width={"175px"} height={"32px"} />
            </SkeletonContainer>
          )}
        </CenterContainer>
        <RightContainer>{right}</RightContainer>
      </LoginToolBar>
    </AppBar>
  );
};
